/**
 * @overview
 * Track elements with a `data-visibility-tracker` attribute.
 * The element is tracked whenever it is visible and is scrolled into view.
 *
 * Each tracker element needs the following attributes:
 * - `data-visibility-tracker`: Enable tracking for an element. Value is the Event Name reported to Analytics.
 * - `data-visibility-tracker-one-shot`: Only emit the tracking event once.
 * - `data-visibility-tracker-arg-*`: Additional attributes beginning this way will be passed to the tracker payload.
 */

import {analytics} from "./analytics";
import {snakeCase} from "lodash";

/**
 * Parse data from the element and submit the tracking event.
 * @param {HTMLElement} trackedElement: Element with a data-visibility-tracker attribute
 * @param {function} stopObservingCallback: Callable that will stop future events from being fired for this element.
 *      This is used to implement one shot functionality.
 */
const trackVisibleTag = (trackedElement, stopObservingCallback) => {
    const prefix = 'visibilityTracker';
    const dataOneShot = prefix + 'OneShot';
    const dataArg = prefix + 'Arg';

    const isOneShot = dataOneShot in trackedElement.dataset &&
        trackedElement.dataset[dataOneShot] !== 'false';

    const payload = {};
    for (let attr in trackedElement.dataset) {
        if (attr.startsWith(dataArg)) {
            let key = attr.substring(dataArg.length);
            key = snakeCase(key);
            payload[key] = trackedElement.dataset[attr];
        }
    }

    const eventName = trackedElement.dataset[prefix];
    analytics.track(eventName, payload);

    if (isOneShot)
        stopObservingCallback();
};

/**
 * Fire trackVisibleTag whenever a targeted element scrolls into view.
 * @type {IntersectionObserver}
 */
const intersectionObserver = new IntersectionObserver(
    (observedEntries, observer) => {
        observedEntries
            .filter((entry) => entry.isIntersecting)
            .forEach((entry) =>
                trackVisibleTag(entry.target, () => observer.unobserve(entry.target))
            )
    }, {
        threshold: [0.5,]
    }
);

const SELECTOR = '[data-visibility-tracker]';
/**
 * Connect the intersectionObserver to elements as they are added to the DOM.
 * @type {MutationObserver}
 */
const mutationObserver = new MutationObserver((mutations) => {
    mutations
        .filter((mutation) => mutation.type === 'childList')
        .forEach((mutation) => {
            Array.from(mutation.addedNodes)
                .filter((node) => node.nodeType === Node.ELEMENT_NODE)
                .forEach((node) => {
                    if (node.matches(SELECTOR))
                        intersectionObserver.observe(node);
                    for (let el of node.querySelectorAll(SELECTOR))
                        intersectionObserver.observe(el);
                })
        });
});

/**
 * Set up intersectionObserver for elements that are already on the page and activate mutationObserver for elements
 * that may be added later.
 */
window.addEventListener('DOMContentLoaded', () => {
    for (let el of document.querySelectorAll(SELECTOR))
        intersectionObserver.observe(el);
    mutationObserver.observe(document.body, {subtree: true, childList: true,});
});
